import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react"
import type { ReactElement, ReactNode, RefCallback } from "react"
import { useLocalization } from "@fluent/react"
import DOMPurify from "dompurify"
import { Box, Button, Chip, Divider, IconButton, Menu, MenuItem, Paper, Stack, Tooltip, Typography } from "@mui/material"
import type { Theme } from "@mui/material/styles"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import { LpField } from "../../../app/components/LpField"
import { DepartmentCodeBadge } from "../../administration/companyDepartments/components/DepartmentCodeBadge"
import { DashboardTopicDto, isMeetingMinuteContent, OperationalDashboardAreaContentOverviewDto, OperationalDashboardAreaOverviewDto } from "../operationalDashboards"
import type { UpdateOperationalDashboardAreaContentPriorityRequest } from "../operationalDashboards"
import { OpenMeetingMinutesButton } from "./OpenMeetingMinutesButton"
import { AreaRatingSelector, AreaRatingValue, ratingDotColors } from "./AreaRatingSelector"
import type { OverviewColorMode } from "./OverviewColorModeMenu"
import { alpha } from "@mui/material/styles"
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined"
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"
import CircularProgress from "@mui/material/CircularProgress"
import OpenInFullIcon from "@mui/icons-material/OpenInFull"
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"

// Ustawienie wysokości kafelka w podglądzie (px). Pełny widok nie ma limitu.
// Dla 3 tematów z jedną linią opisu minimum wynosi 360 px; mniejsze wartości są podnoszone do minimum,
// aby zachować nagłówek, każdy tytuł i opis oraz sekcję decyzji.
// Przy obsłudze dotykiem dochodzi 80 px na większe przyciski.
const TILE_MAX_HEIGHT = 360

const tilePreviewCount = 3
// Większa liczba linii tytułu wymaga też zwiększenia miejsca w wierszu tytułu.
const tileContentTitleLines = 1
const tileContentDescriptionLineHeight = 21
const tileLayout = {
    header: 88,
    decision: 72,
}
const tileContentHeight = (descriptionLines: number) => Math.max(
    198 + tilePreviewCount * Math.max(0, descriptionLines - 1) * tileContentDescriptionLineHeight,
    TILE_MAX_HEIGHT - tileLayout.header - tileLayout.decision - 2,
)

export type Tone = "primary" | "success" | "warning" | "error" | "info" | "neutral"
export type View = "list" | "tiles"

const departmentBackgroundOpacity = 0.03

export const toneColor = (theme: Theme, tone: Tone) =>
    tone === "neutral" ? theme.palette.text.secondary : theme.palette[tone].main

const softChip = (theme: Theme, tone: Tone) => {
    const color = toneColor(theme, tone)
    return {
        color,
        bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.16 : 0.09),
        border: "1px solid",
        borderColor: alpha(color, 0.24),
        borderRadius: 1.25,
        fontWeight: 600,
    }
}

const topicTone = (status: string): Tone =>
    status === "FINISHED" ? "success"
        : ["CANCELED", "CANCELLED"].includes(status) ? "neutral"
            : ["ACTIVE", "IN_PROGRESS"].includes(status) ? "info"
                : status === "NEW" ? "primary" : "neutral"

const meetingTimeZone = "Europe/Warsaw"

const dateKey = (date: Date) => {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: meetingTimeZone, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(date)
    const part = (type: string) => parts.find(value => value.type === type)?.value ?? ""
    return `${part("year")}-${part("month")}-${part("day")}`
}

export const isOpen = (topic: DashboardTopicDto) =>
    !["FINISHED", "CANCELED", "CANCELLED"].includes(topic.status?.toUpperCase() ?? "")

export const isOverdue = (topic: DashboardTopicDto, now: Date) => {
    if (!topic.deadline || !isOpen(topic)) return false
    const deadline = new Date(topic.deadline)
    return !Number.isNaN(deadline.getTime()) && dateKey(deadline) < dateKey(now)
}

export const collectTasks = (items: DashboardTopicDto[]) => {
    const tasks = new Map<string, DashboardTopicDto>()
    const visit = (topic: DashboardTopicDto) => {
        if (topic.itemType.toUpperCase() === "TASK") {
            const key = topic.sharedGuid && topic.sharedGuid !== "00000000-0000-0000-0000-000000000000"
                ? topic.sharedGuid : String(topic.id)
            tasks.set(key, topic)
        }
        topic.tasks.forEach(visit)
    }
    items.forEach(visit)
    return [...tasks.values()]
}

export const topicSearchText = (topic: DashboardTopicDto): string =>
    [topic.name, topic.responsiblePerson, ...topic.tasks.map(topicSearchText)].join(" ")

const sanitizeDashboardText = (value: string) => DOMPurify.sanitize(value, {
    ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "s", "ul", "ol", "li", "blockquote", "a", "div", "span"],
    ALLOWED_ATTR: ["href", "title"],
})

const previewLines = (lines: number) => ({
    display: "-webkit-box",
    WebkitBoxOrient: "vertical" as const,
    WebkitLineClamp: lines,
    overflow: "hidden",
})

const singleLinePreview = {
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap" as const,
    textOverflow: "ellipsis",
}

const ClippedPreview = ({ preview, text, onExpand, children, sx }: {
    preview: boolean
    text: string
    onExpand?: () => void
    children: (ref: RefCallback<HTMLElement>) => ReactNode
    sx?: object
}) => {
    const { l10n } = useLocalization()
    const elementRef = useRef<HTMLElement | null>(null)
    const [clipped, setClipped] = useState(false)
    const setElement = useCallback<RefCallback<HTMLElement>>(element => {
        elementRef.current = element
    }, [])

    useLayoutEffect(() => {
        if (!preview || !onExpand) return
        const element = elementRef.current
        if (!element) return
        let active = true
        const measure = () => {
            if (!active) return
            const next = element.scrollWidth > element.clientWidth + 1
                || element.scrollHeight > element.clientHeight + 1
            setClipped(previous => previous === next ? previous : next)
        }
        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(element)
        document.fonts?.ready.then(measure)
        document.fonts?.addEventListener("loadingdone", measure)
        return () => {
            active = false
            observer.disconnect()
            document.fonts?.removeEventListener("loadingdone", measure)
        }
    }, [preview, text, onExpand])

    if (!preview || !onExpand) return <>{children(setElement)}</>

    const expandLabel = l10n.getString("operational-dashboard-show-full-content", null, "_Rozwiń kafelek — pokaż pełną treść")
    return (
        <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 28px", columnGap: 0.5, position: "relative", minWidth: 0, ...sx }}>
            {children(setElement)}
            {clipped && (
                <Tooltip title={expandLabel}>
                    <IconButton size="small" aria-label={expandLabel} onClick={onExpand}
                        sx={theme => ({
                            position: "absolute", right: 0, bottom: 0,
                            width: 28, height: 20, p: 0, borderRadius: 1,
                            color: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.22) },
                            "&.Mui-focusVisible": {
                                outline: `2px solid ${theme.palette.primary.main}`,
                                outlineOffset: 1,
                            },
                        })}>
                        <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    )
}

const dashboardPreviewText = (value: string) => {
    const body = DOMPurify.sanitize(value, { RETURN_DOM_FRAGMENT: true })
    const textFrom = (parent: Node): string => {
        let text = ""
        let previousBlock = false
        parent.childNodes.forEach(node => {
            const element = node instanceof Element ? node : null
            const block = element?.matches("p, div, li, blockquote, ul, ol, pre, h1, h2, h3, h4, h5, h6") ?? false
            const content = element?.tagName === "BR" ? "\n"
                : element ? textFrom(element) : node.textContent ?? ""
            if (!content && !block) return
            if ((block || previousBlock) && text && !text.endsWith("\n")) {
                text += "\n"
            }
            text += content || "\n"
            previousBlock = block
        })
        return text
    }
    return textFrom(body)
}

// Podgląd łączy akapity i listy w jedną linię; CSS dodaje wielokropek.
// Pełny widok nadal wyświetla oryginalny, oczyszczony HTML.
const compactPreviewText = (value: string) =>
    dashboardPreviewText(value).replace(/\s+/g, " ").trim()

// Lewy pasek nadal pokazuje kolor działu/statusu.
const tileHighlight = (theme: Theme) => ({
    borderTopColor: alpha(theme.palette.primary.main, 0.8),
    borderRightColor: alpha(theme.palette.primary.main, 0.8),
    borderBottomColor: alpha(theme.palette.primary.main, 0.8),
    outline: `2px solid ${alpha(theme.palette.primary.main, 0.7)}`,
    outlineOffset: 2,
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.22)}`,
    "& .area-expand-button": {
        bgcolor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderColor: theme.palette.primary.main,
        boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.18)}`,
    },
})

export const areaRating = (area: OperationalDashboardAreaOverviewDto, hasMeeting: boolean) =>
    !hasMeeting ? "NO_MEETING"
        : area.meetingAgendaItemId != null ? area.rating?.toUpperCase() ?? "NOT_ASSESSED" : "NO_AGENDA"

const ratingTone = (rating: string): Tone =>
    rating === "RED" ? "error" : rating === "YELLOW" ? "warning" : rating === "GREEN" ? "success" : "neutral"

export const formatTopicDeadline = (deadline: string | null, locale: string, fallback: string) => {
    if (!deadline) return fallback
    const date = new Date(deadline)
    return Number.isNaN(date.getTime()) ? fallback
        : new Intl.DateTimeFormat(locale === "pl" ? "pl-PL" : locale === "de" ? "de-DE" : "en-US", {
            timeZone: meetingTimeZone, day: "2-digit", month: "2-digit", year: "numeric",
        }).format(date)
}

export const AreaStatusChip = ({ rating, dotOnly = false }: { rating: string; dotOnly?: boolean }) => {
    const { l10n } = useLocalization()
    const label = (key: string, fallback: string) => l10n.getString(`operational-dashboard-${key}`, null, `_${fallback}`)
    const tone = ratingTone(rating)
    const ratingLabel = rating === "NO_AGENDA" ? label("outside-agenda", "Poza agendą")
        : rating === "NO_MEETING" ? label("rating-no-meeting", "Brak spotkania")
            : rating === "GREEN" ? label("rating-green", "Pod kontrolą")
                : rating === "YELLOW" ? label("rating-yellow", "Wymaga uwagi")
                    : rating === "RED" ? label("rating-red", "Krytyczny")
                        : rating === "NOT_ASSESSED" ? l10n.getString("not-assessed", null, "_Nie oceniono") : rating

    if (dotOnly) return (
        <Tooltip title={ratingLabel}>
            <Box component="span" role="img" aria-label={ratingLabel} sx={{
                width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                bgcolor: tone === "neutral" ? "text.disabled" : `${tone}.main`,
            }} />
        </Tooltip>
    )

    return <Chip size="small" variant="filled" label={ratingLabel} sx={theme => ({
        ...softChip(theme, tone), border: "none", borderRadius: 1, flexShrink: 0, maxWidth: "50%",
    })} />
}

export const TopicList = ({ topics, now, locale }: { topics: DashboardTopicDto[]; now: Date; locale: string }) => {
    const { l10n } = useLocalization()
    const label = (key: string, fallback: string) => l10n.getString(`operational-dashboard-${key}`, null, `_${fallback}`)

    if (!topics.length) return <Typography variant="body2" color="text.secondary">{label("no-topics", "Brak tematów i zadań.")}</Typography>

    return (
        <Stack spacing={1}>
            {topics.map(topic => {
                const overdue = isOverdue(topic, now)
                const dateLabel = formatTopicDeadline(topic.deadline, locale, l10n.getString("no-deadline", null, "_Bez terminu"))
                const status = topic.status?.toUpperCase() ?? ""

                return (
                    <Paper key={`${topic.itemType}-${topic.id}`} variant="outlined" sx={theme => ({
                        p: 1.5, borderRadius: 2, backgroundImage: "none",
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.09 : 0.035),
                        borderColor: overdue ? alpha(theme.palette.error.main, 0.35) : "divider",
                    })}>
                        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                            <Typography variant="overline" color="text.secondary">
                                {topic.itemType.toUpperCase() === "TASK" ? label("task", "Zadanie") : label("topic", "Temat")}
                            </Typography>
                            <Chip
                                size="small"
                                variant="filled"
                                sx={theme => softChip(theme, topicTone(status))}
                                label={status ? label(`status-${status.toLowerCase()}`, topic.status ?? status) : l10n.getString("no-status", null, "_Brak statusu")}
                            />
                            {overdue && <Chip size="small" variant="filled" sx={theme => softChip(theme, "error")} label={label("overdue", "Po terminie")} />}
                            {topic.priority && <Typography variant="caption" color="text.secondary">{l10n.getString("priority", null, "_Priorytet")}: {topic.priority}</Typography>}
                        </Stack>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, overflowWrap: "anywhere" }}>{topic.name}</Typography>
                        <Typography variant="caption" color={overdue ? "error.main" : "text.secondary"}>
                            {topic.responsiblePerson || l10n.getString("unassigned", null, "_Nie przypisano")} · {dateLabel}
                        </Typography>
                        {topic.tasks.length > 0 && (
                            <Box sx={{ pl: 2, mt: 1, borderLeft: 1, borderColor: "divider" }}>
                                <TopicList topics={topic.tasks} now={now} locale={locale} />
                            </Box>
                        )}
                    </Paper>
                )
            })}
        </Stack>
    )
}

const AreaEditHistoryActions = ({ onEdit, onHistory, showEdit = false }: {
    onEdit?: () => void
    onHistory?: () => void
    showEdit?: boolean
}) => {
    const { l10n } = useLocalization()
    const label = (key: string, fallback: string) => l10n.getString(`operational-dashboard-${key}`, null, `_${fallback}`)

    return (
        <Stack direction="row" alignItems="center" sx={{
            flexShrink: 0, ml: "auto",
            "& .MuiIconButton-root": {
                width: 28, height: 28, p: 0.5,
                "@media (pointer: coarse)": { width: 44, height: 44 },
            },
            "& .MuiSvgIcon-root": { fontSize: 17 },
        }}>
            {showEdit && (
                <Tooltip title={onEdit ? l10n.getString("edit", null, "_Edytuj") : label("content-edit-unavailable", "Edycja tej treści nie jest jeszcze dostępna.")}>
                    <span><IconButton size="small" disabled={!onEdit} onClick={onEdit} aria-label={l10n.getString("edit", null, "_Edytuj")}>
                        <EditOutlinedIcon fontSize="small" />
                    </IconButton></span>
                </Tooltip>
            )}
            <Tooltip title={l10n.getString("history", null, "_Historia")}>
                <span><IconButton size="small" disabled={!onHistory} onClick={onHistory} aria-label={l10n.getString("history", null, "_Historia")}>
                    <HistoryOutlinedIcon fontSize="small" />
                </IconButton></span>
            </Tooltip>
        </Stack>
    )
}

export const AreaDiscussionAction = ({ canDiscuss, discussed, onDiscuss, discussing = false, discussionBusy = false }: {
    canDiscuss: boolean
    discussed: boolean
    onDiscuss?: () => void
    discussing?: boolean
    discussionBusy?: boolean
}) => {
    const { l10n } = useLocalization()
    const label = (key: string, fallback: string) => l10n.getString(`operational-dashboard-${key}`, null, `_${fallback}`)

    if (!discussed && (!canDiscuss || !onDiscuss)) return null

    return (
        <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: "divider", display: "flex", justifyContent: "flex-end" }}>
            {canDiscuss && onDiscuss ? (
                <Button size="small" variant="outlined" color="success"
                    startIcon={discussing ? <CircularProgress size={16} color="inherit" />
                        : discussed ? <CheckCircleIcon /> : <CheckCircleOutlineIcon />}
                    disabled={discussionBusy} onClick={onDiscuss}>
                    {discussed ? label("unmark-discussed", "Cofnij oznaczenie jako omówione")
                        : label("mark-discussed", "Oznacz jako omówione")}
                </Button>
            ) : (
                <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />}
                    label={label("discussed", "Omówiono")} />
            )}
        </Box>
    )
}

type PriorityValue = UpdateOperationalDashboardAreaContentPriorityRequest["priority"]

const PriorityDots = ({ priority, label, onChange, busy }: {
    priority: string | null
    label: string
    onChange?: (value: PriorityValue) => void
    busy?: boolean
}) => {
    const { l10n } = useLocalization()
    const menuId = useId()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const selected = priority?.toUpperCase()
    const options = [
        { value: "L", label: l10n.getString("priority-low", null, "_Niski"), ...ratingDotColors.GREEN },
        { value: "M", label: l10n.getString("priority-medium", null, "_Średni"), ...ratingDotColors.YELLOW },
        { value: "H", label: l10n.getString("priority-high", null, "_Wysoki"), ...ratingDotColors.RED },
    ] as const
    const current = options.find(option => option.value === selected)
    const priorityLabel = current?.label ?? priority ?? l10n.getString("not-set", null, "_Nie ustawiono")

    useEffect(() => {
        if (!onChange || busy) setAnchorEl(null)
    }, [onChange, busy])

    const indicator = (
        <Box component="span" sx={theme => ({
            width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
            bgcolor: current?.color ?? theme.palette.text.disabled,
            outline: current ? `2px solid ${current.borderColor}` : "none",
            outlineOffset: 1,
        })} />
    )

    return (
        <>
            <Tooltip title={`${label}: ${priorityLabel}`}
                disableHoverListener={Boolean(anchorEl)} disableFocusListener={Boolean(anchorEl)}>
                <Box component="span" sx={{
                    display: "inline-flex",
                    flexShrink: 0,
                    transform: "translateY(-2px)",
                }}>
                    {onChange ? (
                        <IconButton size="small" disabled={busy} aria-label={`${label}: ${priorityLabel}`}
                            aria-haspopup="menu" aria-expanded={Boolean(anchorEl) && !busy}
                            aria-controls={anchorEl ? menuId : undefined} aria-busy={busy || undefined}
                            onClick={event => setAnchorEl(event.currentTarget)}
                            sx={{
                                width: 24, height: 24, p: 0, borderRadius: 1,
                                "&:hover": { bgcolor: current ? alpha(current.color, 0.12) : "action.hover" },
                                "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "text.primary", outlineOffset: 1 },
                            }}>
                            {indicator}
                        </IconButton>
                    ) : (
                        <Box component="span" role="img" aria-label={`${label}: ${priorityLabel}`}
                            sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28 }}>
                            {indicator}
                        </Box>
                    )}
                </Box>
            </Tooltip>
            {onChange && (
                <Menu id={menuId} anchorEl={anchorEl} open={Boolean(anchorEl) && !busy}
                    onClose={() => setAnchorEl(null)} MenuListProps={{ "aria-label": label }}>
                    {options.map(option => (
                        <MenuItem key={option.value} selected={selected === option.value}
                            onClick={() => {
                                setAnchorEl(null)
                                if (selected !== option.value) onChange(option.value)
                            }}
                            sx={{ gap: 1, minWidth: 150 }}>
                            <Box component="span" sx={{
                                width: 12, height: 12, flexShrink: 0, borderRadius: "50%",
                                bgcolor: option.color, outline: `2px solid ${option.borderColor}`, outlineOffset: 1,
                            }} />
                            {option.label}
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </>
    )
}

export const AreaContents = ({ contents, large = false, compact = false, onExpand, onEditContent, onViewContentHistory,
    onChangePriority, priorityBusy, showPriorityDots = true, descriptionLines = 2 }: {
        contents: OperationalDashboardAreaOverviewDto["contents"]
        large?: boolean
        compact?: boolean
        onExpand?: () => void
        onEditContent?: (content: OperationalDashboardAreaContentOverviewDto) => void
        onViewContentHistory?: (content: OperationalDashboardAreaContentOverviewDto) => void
        onChangePriority?: (content: OperationalDashboardAreaContentOverviewDto, value: PriorityValue) => void
        priorityBusy?: boolean
        showPriorityDots?: boolean
        descriptionLines?: number
    }) => {
    const { l10n } = useLocalization()
    const orderedContents = [...contents].sort((a, b) => a.displayOrder - b.displayOrder || a.id - b.id)

    if (!orderedContents.length) {
        return <Typography variant={large ? "body1" : "body2"} sx={{ fontStyle: "italic" }} color="text.secondary">
            {l10n.getString("operational-dashboard-no-topics", null, "_Brak tematów i zadań.")}
        </Typography>
    }

    return (
        <Box component="ol" sx={{
            m: 0, p: 0, listStyle: "none",
            display: "grid", rowGap: compact ? 1 : large ? 2 : 1.25,
            ...(compact ? {
                height: "100%",
                minHeight: 0,
                gridTemplateRows: `repeat(${tilePreviewCount}, minmax(0, 1fr))`,
                overflow: "hidden",
            } : {}),
        }}>
            {(compact ? orderedContents.slice(0, tilePreviewCount) : orderedContents).map((content, index) => {
                const descriptionPreview = compact && content.description
                    ? compactPreviewText(content.description) : ""

                return (
                    <Box component="li" key={content.id} sx={{
                        display: "grid",
                        gridTemplateColumns: `${large ? 22 : 18}px minmax(0, 1fr) auto`,
                        columnGap: large ? 1 : 0.75,
                        rowGap: 0.25,
                        alignItems: "start",
                        minWidth: 0,
                        ...(compact ? {
                            minHeight: 0,
                            overflow: "hidden",
                            gridTemplateRows: "28px minmax(0, 1fr)",
                            "@media (pointer: coarse)": {
                                gridTemplateRows: "44px minmax(0, 1fr)",
                            },
                        } : {}),
                    }}>
                        <LpField value={index + 1} sx={{
                            width: large ? 22 : 18, height: large ? 22 : 18,
                            fontSize: large ? 11 : 10, borderRadius: 1,
                            marginTop: large ? 1 : 0.5,
                        }} />

                        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 0.75 }}>
                            <ClippedPreview preview={compact} text={content.name} onExpand={onExpand} sx={{ flex: 1 }}>
                                {ref => <Typography ref={ref} component="div" variant={large ? "body1" : "body2"} sx={{
                                    flex: 1, minWidth: 0, pt: 0.375, fontWeight: 600,
                                    lineHeight: 1.4, overflowWrap: "anywhere",
                                    ...(compact ? previewLines(tileContentTitleLines) : {}),
                                }}>
                                    {content.name}
                                    {showPriorityDots && content.item && (
                                        <PriorityDots priority={content.item.priority}
                                            label={l10n.getString("priority", null, "_Priorytet")}
                                            onChange={isMeetingMinuteContent(content) && onChangePriority
                                                ? value => onChangePriority(content, value) : undefined}
                                            busy={priorityBusy} />
                                    )}
                                </Typography>}

                            </ClippedPreview>

                        </Box>

                        <AreaEditHistoryActions
                            showEdit={!!onEditContent}
                            onEdit={onEditContent && isMeetingMinuteContent(content)
                                ? () => onEditContent(content) : undefined}
                            onHistory={onViewContentHistory
                                ? () => onViewContentHistory(content) : undefined}
                        />

                        {(compact ? !!descriptionPreview : !!content.description) && (
                            <ClippedPreview preview={compact} text={compact ? descriptionPreview : content.description ?? ""}
                                onExpand={onExpand} sx={{ gridColumn: "2 / -1", alignSelf: "start" }}>
                                {ref => <Typography ref={ref} component="div" variant={large ? "body1" : "body2"}
                                    color="text.secondary"
                                    sx={{
                                        gridColumn: compact ? undefined : "2 / -1",
                                        minWidth: 0,
                                        lineHeight: 1.45,
                                        whiteSpace: "pre-wrap",
                                        overflowWrap: "anywhere",
                                        ...(compact ? {
                                            ...(descriptionLines === 1
                                                ? singleLinePreview : previewLines(descriptionLines)),
                                            alignSelf: "start",
                                        } : {}),
                                        "& p": { my: 0.25 },
                                        "& p:first-child": { mt: 0 },
                                        "& p:last-child": { mb: 0 },
                                        "& ul, & ol": { pl: 2.25, my: 0.5 },
                                        "& blockquote": {
                                            ml: 0, mr: 0, my: 0.5, pl: 1,
                                            borderLeft: "2px solid", borderColor: "divider",
                                        },
                                    }}
                                    dangerouslySetInnerHTML={compact ? undefined : {
                                        __html: sanitizeDashboardText(content.description ?? ""),
                                    }}
                                >
                                    {compact ? descriptionPreview : undefined}
                                </Typography>}
                            </ClippedPreview>
                        )}
                    </Box>
                )
            })}
        </Box>
    )
}

export const AreaDecision = ({
    summary,
    large = false,
    compact = false,
    onExpand,
    onEdit,
    onHistory,
    expandAction,
}: {
    summary: string | null
    // Zachowany prop, aby dotychczasowe wywołania pozostały poprawne.
    departmentColor?: string | null
    large?: boolean
    compact?: boolean
    onExpand?: () => void
    onEdit?: () => void
    onHistory?: () => void
    expandAction?: ReactNode
}) => {
    const { l10n } = useLocalization()

    const summaryPreview = compact && summary ? compactPreviewText(summary) : ""
    const summaryText = summaryPreview || l10n.getString("operational-dashboard-no-entry", null, "_Brak wpisu")
    const decisionLabel = l10n.getString("operational-dashboard-decision-action", null, "_Decyzje / akcje / ryzyka")

    return (
        <Box sx={theme => ({
            px: large ? 2 : 1.5,
            py: compact ? 1 : large ? 1.5 : 1.25,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.055 : 0.025),
            minHeight: 0,
            ...(compact ? {
                height: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflow: "hidden",
                display: "grid",
                ...(expandAction ? { gridTemplateColumns: "minmax(0, 1fr) auto" } : {}),
                gridTemplateRows: "28px minmax(0, 1fr)",
                rowGap: 0.375,
                "@media (pointer: coarse)": {
                    gridTemplateRows: "44px minmax(0, 1fr)",
                },
            } : {}),
        })}>
            <Stack direction="row" alignItems="center" gap={0.75}
                sx={{
                    minWidth: 0, mb: !compact && summary?.trim() ? 0.375 : 0,
                    ...(compact && expandAction ? { gridColumn: "1 / -1" } : {})
                }}>
                <SubdirectoryArrowRightIcon sx={{
                    fontSize: 17, flexShrink: 0, color: "text.secondary",
                }} />
                <ClippedPreview preview={compact} onExpand={onExpand} text={decisionLabel} sx={{ flex: 1 }}>
                    {ref => <Typography ref={ref} component="div" variant="caption" sx={{
                        flex: 1, minWidth: 0, fontWeight: 600,
                        color: "text.primary", lineHeight: 1.4,
                        overflowWrap: "anywhere",
                        ...(compact ? previewLines(1) : {}),
                    }}>
                        {decisionLabel}
                    </Typography>}
                </ClippedPreview>
                <AreaEditHistoryActions showEdit={!!onEdit} onEdit={onEdit} onHistory={onHistory} />
            </Stack>

            {(compact || summary?.trim()) && (
                <ClippedPreview preview={compact} onExpand={onExpand} text={summaryText}
                    sx={{ ml: 3 }}>
                    {ref => <Typography ref={ref} component="div" variant={large ? "body1" : "body2"} sx={{
                        ml: compact ? 0 : 3,
                        color: "text.primary",
                        fontWeight: 400,
                        lineHeight: 1.45,
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        "& p": { my: 0.25 },
                        "& p:first-child": { mt: 0 },
                        "& p:last-child": { mb: 0 },
                        "& ul, & ol": { pl: 2.25, my: 0.5 },
                        "& blockquote": {
                            ml: 0, mr: 0, my: 0.5, pl: 1,
                            borderLeft: "2px solid", borderColor: "divider",
                        },
                        ...(compact ? {
                            ...singleLinePreview,
                            alignSelf: "start",
                            color: summaryPreview ? "text.primary" : "text.secondary",
                            fontStyle: summaryPreview ? "normal" : "italic",
                        } : {}),
                    }} dangerouslySetInnerHTML={compact ? undefined : { __html: sanitizeDashboardText(summary ?? "") }}>
                        {compact ? summaryText : undefined}
                    </Typography>}
                </ClippedPreview>
            )}
            {expandAction && (
                <Box sx={{
                    display: "flex", justifyContent: "flex-end", alignSelf: "end",
                    ...(compact ? { gridColumn: 2, gridRow: 2, ml: 0.5 } : { mt: 1 }),
                }}>
                    {expandAction}
                </Box>
            )}
        </Box>
    )
}

export type AreaPanelProps = {
    area: OperationalDashboardAreaOverviewDto
    showDiscussionStatus: boolean
    hasMeeting: boolean
    inMeeting: boolean
    colorMode: OverviewColorMode
    meetingMinutesId: string | null
    editMode: boolean
    onEdit: () => void
    onEditContent?: (content: OperationalDashboardAreaContentOverviewDto) => void
    onChangePriority?: (content: OperationalDashboardAreaContentOverviewDto, value: PriorityValue) => void
    priorityBusy?: boolean
    showPriorityDots?: boolean
    descriptionLines?: number
    onViewContentHistory?: (content: OperationalDashboardAreaContentOverviewDto) => void
    onEditSummary?: () => void
    onViewAgendaHistory?: () => void
    onToggleDiscussed?: () => void
    discussing?: boolean
    discussionBusy?: boolean
    onChangeRating?: (rating: AreaRatingValue) => void
    ratingSaving?: boolean
    ratingBusy?: boolean
    showAreaStatus?: boolean
    compact?: boolean
    expanded: boolean
    onToggleExpand: () => void
}

export const AreaPanel = (props: AreaPanelProps): ReactElement => {
    const {
        area, showDiscussionStatus, hasMeeting, inMeeting, colorMode, meetingMinutesId, editMode, onEdit,
        onEditContent, onViewContentHistory, onEditSummary, onViewAgendaHistory, onToggleDiscussed,
        onChangePriority, priorityBusy, showPriorityDots = true, descriptionLines = 2,
        discussing = false, discussionBusy = false, onChangeRating, ratingSaving = false, ratingBusy = false,
        showAreaStatus = true, compact = true, expanded, onToggleExpand,
    } = props
    const { l10n } = useLocalization()
    const detailsId = useId()
    const preview = compact && !expanded
    const label = (key: string, fallback: string) => l10n.getString(`operational-dashboard-${key}`, null, `_${fallback}`)
    const rating = areaRating(area, hasMeeting)
    const tone = showAreaStatus ? ratingTone(rating) : "neutral"
    const departmentColor = area.departmentColor?.trim()
    const discussed = area.discussionStatus?.toUpperCase() === "DISCUSSED"
    const canDiscuss = inMeeting && area.meetingAgendaItemId != null && !!onToggleDiscussed
    const discussionLabel = discussed
        ? canDiscuss ? label("unmark-discussed", "Cofnij oznaczenie jako omówione") : label("discussed", "Omówiono")
        : canDiscuss ? label("mark-discussed", "Oznacz jako omówione") : label("not-discussed", "Nie omówiono")
    const remainingCount = Math.max(0, area.contents.length - tilePreviewCount)
    const expandLabel = expanded ? l10n.getString("collapse", null, "_Zwiń")
        : l10n.getString("show-all", null, "_Pokaż całość")
    const expandTooltip = expanded ? expandLabel : remainingCount > 0
        ? `${expandLabel} · ${l10n.getString("operational-dashboard-more-items", { count: remainingCount },
            `_Pozostałe pozycje: ${remainingCount}`)}`
        : expandLabel

    const title = (
        <Stack direction="row" alignItems="center" gap={0.75}
            sx={{ minWidth: 0, minHeight: 0, flex: 1, ...(preview ? { overflow: "hidden" } : {}) }}>
            {area.departmentId != null && area.departmentCode != null && (
                <Box sx={{ display: "flex", flexShrink: 0 }}>
                    <DepartmentCodeBadge code={area.departmentCode} color={area.departmentColor ?? undefined} />
                </Box>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <ClippedPreview preview={preview} text={area.name} onExpand={onToggleExpand}>
                    {ref => <Typography ref={ref} component="h2" variant="h6" sx={{
                        m: 0, minWidth: 0, fontSize: 17, lineHeight: 1.3,
                        fontWeight: 600, overflowWrap: "anywhere", color: "text.primary",
                        ...(preview ? previewLines(1) : {}),
                    }}>
                        {area.name}
                    </Typography>}
                </ClippedPreview>
                {area.description?.trim() && (
                    <ClippedPreview preview={preview} text={area.description} onExpand={onToggleExpand}
                        sx={{ mt: 0.5 }}>
                        {ref => <Typography ref={ref} component="div" variant="caption" color="text.secondary" sx={{
                            display: "block", mt: preview ? 0 : 0.5, overflowWrap: "anywhere", lineHeight: 1.4,
                            whiteSpace: preview ? "normal" : "pre-wrap",
                            ...(preview ? previewLines(1) : {}),
                        }}>
                            {area.description}
                        </Typography>}
                    </ClippedPreview>
                )}
            </Box>
        </Stack>
    )

    const owner = (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, color: "text.secondary" }}>
            <Tooltip title={label("area-owner", "Właściciel obszaru")}>
                <PersonOutlineIcon sx={{ fontSize: 16, flexShrink: 0, color: "primary.main" }} />
            </Tooltip>
            <ClippedPreview preview={preview} text={area.responsibleName || l10n.getString("unassigned", null, "_Nie przypisano")}
                onExpand={onToggleExpand} sx={{ flex: 1 }}>
                {ref => <Typography ref={ref} variant="caption" sx={{
                    overflowWrap: "anywhere", lineHeight: 1.4, minWidth: 0,
                    ...(preview ? previewLines(1) : {}),
                }}>
                    {area.responsibleName || l10n.getString("unassigned", null, "_Nie przypisano")}
                </Typography>}
            </ClippedPreview>
        </Stack>
    )

    const actions = (
        <Stack direction="row" alignItems="center" justifyContent="flex-end"
            useFlexGap flexWrap="nowrap" gap={0.25}
            sx={{ ml: "auto", minWidth: 0, maxWidth: "100%", flexShrink: 0 }}>
            {editMode && (
                <IconButton size="small" onClick={onEdit} aria-label={label("tile-settings", "Ustawienia kafelka")}>
                    <SettingsOutlinedIcon fontSize="small" />
                </IconButton>
            )}
            {showDiscussionStatus && (area.meetingAgendaItemId != null || discussed) && (
                <Tooltip title={discussionLabel}>
                    <span>
                        {canDiscuss ? (
                            <IconButton size="small" color={discussed ? "success" : "default"}
                                disabled={discussionBusy} onClick={onToggleDiscussed}
                                aria-label={discussionLabel} aria-pressed={discussed}>
                                {discussing ? <CircularProgress size={20} color="inherit" />
                                    : discussed ? <CheckCircleIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
                            </IconButton>
                        ) : (
                            <Box component="span" role="img" aria-label={discussionLabel}
                                sx={{ display: "inline-flex", p: 0.5, color: discussed ? "success.main" : "text.secondary" }}>
                                {discussed ? <CheckCircleIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
                            </Box>
                        )}
                    </span>
                </Tooltip>
            )}
            {showAreaStatus && (
                <AreaRatingSelector
                    rating={rating}
                    onChange={value => onChangeRating?.(value)}
                    disabled={!inMeeting || area.meetingAgendaItemId == null || !onChangeRating || ratingBusy}
                    saving={ratingSaving}
                    intensive={false}
                    compact={preview || !inMeeting} />
            )}
            <OpenMeetingMinutesButton meetingMinutesId={meetingMinutesId} agendaItemId={area.meetingAgendaItemId} iconOnly />
        </Stack>
    )

    return (
        <Paper component="article" variant="outlined"
            sx={theme => ({
                borderRadius: 2.5, overflow: "hidden", minWidth: 0, minHeight: 0,
                ...(preview ? {
                    "--tile-header": `${tileLayout.header}px`,
                    "--tile-contents": `${tileContentHeight(descriptionLines)}px`,
                    "--tile-decision": `${tileLayout.decision}px`,
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr)",
                    gridTemplateRows: "var(--tile-header) var(--tile-contents) var(--tile-decision)",
                    boxSizing: "border-box",
                    // 2 px uwzględniają górną i dolną ramkę Paper.
                    height: "calc(var(--tile-header) + var(--tile-contents) + var(--tile-decision) + 2px)",
                    alignSelf: "start",
                    "@media (pointer: coarse)": {
                        "--tile-header": `${tileLayout.header + 16}px`,
                        "--tile-contents": `${tileContentHeight(descriptionLines) + tilePreviewCount * 16}px`,
                        "--tile-decision": `${tileLayout.decision + 36}px`,
                    },
                } : {}),
                bgcolor: "background.paper",
                backgroundImage: departmentColor
                    ? `linear-gradient(${alpha(departmentColor, departmentBackgroundOpacity)}, ${alpha(departmentColor, departmentBackgroundOpacity)})`
                    : colorMode === "plum" ? "none" : `linear-gradient(110deg, ${alpha(toneColor(theme, tone), 0.045)}, transparent 48%)`,
                borderColor: theme.palette.divider,
                borderLeft: "3px solid",
                borderLeftColor: departmentColor || (tone === "neutral" ? theme.palette.divider : toneColor(theme, tone)),
                boxShadow: theme.palette.mode === "dark" ? "none" : "0 3px 14px rgba(33, 51, 77, 0.035)",
                ...(compact ? {
                    transition: theme.transitions.create(["border-color", "box-shadow"], { duration: 160 }),
                    "@media (hover: hover) and (pointer: fine)": {
                        "&:hover": tileHighlight(theme),
                        "&:hover .area-expand-label": { maxWidth: 160, opacity: 1, ml: 0.5 },
                    },
                    "&:focus-within": tileHighlight(theme),
                    "&:focus-within .area-expand-label": { maxWidth: 160, opacity: 1, ml: 0.5 },
                    "@media (prefers-reduced-motion: reduce)": {
                        transition: "none",
                        "& .area-expand-button": { transition: "none" },
                        "& .area-expand-label": { transition: "none" },
                    },
                } : {}),
            })}>
            <Box sx={{
                px: 1.5, pt: preview ? 1 : 1.5,
                ...(preview ? {
                    height: "100%",
                    minWidth: 0,
                    minHeight: 0,
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                } : {}),
            }}>
                {/* Stały układ: tytuł i akcje obok siebie, właściciel bezpośrednio pod nimi. */}
                <Stack direction="row" alignItems="center"
                    justifyContent="space-between" gap={1}
                    sx={{ minWidth: 0, minHeight: 0 }}>
                    {title}
                    {actions}
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}
                    sx={{ mt: 0.75, minWidth: 0, flexShrink: 0 }}>
                    {owner}
                </Stack>
                <Divider sx={{ mt: preview ? "auto" : 1, flexShrink: 0 }} />
            </Box>

            <Box id={detailsId} sx={{
                px: 1.5, pt: 1, pb: 1.5, minWidth: 0, minHeight: 0,
                ...(preview ? { boxSizing: "border-box", overflow: "hidden" } : {}),
            }}>
                <AreaContents contents={area.contents} compact={preview} onExpand={onToggleExpand} onEditContent={onEditContent}
                    onChangePriority={onChangePriority} priorityBusy={priorityBusy} showPriorityDots={showPriorityDots}
                    descriptionLines={descriptionLines}
                    onViewContentHistory={onViewContentHistory} />
            </Box>

            <AreaDecision
                summary={area.summary}
                compact={preview}
                onExpand={onToggleExpand}
                departmentColor={departmentColor}
                onEdit={area.meetingAgendaItemId != null ? onEditSummary : undefined}
                onHistory={onViewAgendaHistory}
                expandAction={compact && (
                    <Button className="area-expand-button" size="small" variant="text"
                        startIcon={expanded ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                        onClick={onToggleExpand}
                        aria-label={`${expandTooltip}: ${area.name}`}
                        aria-expanded={expanded}
                        aria-controls={detailsId}
                        sx={theme => ({
                            flexShrink: 0, minWidth: 0, height: 20, minHeight: 20,
                            px: 0.75, py: 0, borderRadius: 1.25,
                            textTransform: "none", whiteSpace: "nowrap",
                            fontSize: 10, lineHeight: 1.2, fontWeight: 600,
                            color: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.09),
                            border: "1px solid",
                            borderColor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.55 : 0.38),
                            boxShadow: "none",
                            transition: theme.transitions.create(
                                ["background-color", "border-color", "color", "box-shadow"],
                                { duration: 160 },
                            ),
                            "& .MuiButton-startIcon": { ml: 0, mr: 0 },
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                            "& .area-expand-label": {
                                display: "inline-block", maxWidth: 0, opacity: 0, overflow: "hidden",
                                transition: theme.transitions.create(["max-width", "opacity", "margin-left"],
                                    { duration: 160 }),
                            },
                            "&.Mui-focusVisible": {
                                outline: `2px solid ${theme.palette.primary.main}`,
                                outlineOffset: 2,
                            },
                            "@media (pointer: coarse)": {
                                height: 44, minHeight: 44,
                                "& .area-expand-label": { maxWidth: 160, opacity: 1, ml: 0.5 },
                            },
                        })}>
                        <Box component="span" className="area-expand-label">{expandLabel}</Box>
                        {preview && remainingCount > 0 && (
                            <Box component="span" aria-hidden="true" sx={{
                                ml: 0.5, px: 0.5, borderRadius: 0.75, bgcolor: "action.hover",
                            }}>+{remainingCount}</Box>
                        )}
                    </Button>
                )}
            />

        </Paper>
    )
}
